/**
 * Discover's search state (#2290): the box's text, and everything read from it.
 *
 * The text is the one state. The tokens and their highlighting, the chips,
 * the Filters panel's fields, the query sent and the URL are all derived from
 * it, and the panel, the chips, the help's examples and the URL all change
 * the search by writing the text. So the views cannot disagree.
 *
 * Plain words go through the same `POST api/models/query` as filters (a
 * `{text}` query is today's `$text` + name-prefix search, #2287/#2289), with
 * the same 300 ms pause after typing as before.
 *
 * The URL follows the settled search: a filter change is a new history
 * entry, typing replaces the current one; back and forward re-read the text
 * from the URL.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import {
  chipTokens,
  clearFilters,
  filtersKey,
  isEmptyQuery,
  parseSearch,
  queryKey,
  removeGroup,
  removeToken,
  renameEntity,
  setField,
  tokenProblems,
  type EntityKind,
  type Field,
  type ParsedSearch,
  type SearchQuery,
  type Token,
} from './searchSyntax';
import { DISCOVER_PATH, paramsToText, queryToSearch } from './searchUrl';
import { useEntityResolver, type EntityResolver } from './useEntityResolver';
import { useModelSearch, type ModelSearch } from './useModelSearch';

export const SEARCH_DEBOUNCE_MS = 300;

const norm = (s: string) => (s === '?' ? '' : s);

interface Settled {
  key: string;
  query: SearchQuery;
}

const settledOf = (text: string): Settled => {
  // A URL's text names people, functionals and groups by id: nothing to look up.
  const { query } = parseSearch(text);
  return { key: queryKey(query), query };
};

export interface DiscoverSearch {
  text: string;
  caret: number | null;
  parsed: ParsedSearch;
  /** The filters in force, as chips. */
  chips: Token[];
  /** Something is being searched for: Discover shows results, not its sections. */
  active: boolean;
  /** Problems to show beside the box, in plain words: the box's, then the server's. */
  problems: string[];
  results: ModelSearch;
  /** No answer yet for what is typed (typing, a lookup, or the request). */
  searching: boolean;
  /** The settled search the results are for. */
  settled: SearchQuery;
  resolver: EntityResolver;

  /** Typing: the text and where the caret is after the change. */
  type: (text: string, caret: number) => void;
  /** A change made for the user (panel, chip, help): applied at once, nothing left being typed. */
  replace: (text: string, caret?: number) => void;
  /** Enter, or the caret leaving: the token being typed applies. */
  settle: () => void;
  setCaret: (caret: number | null) => void;
  setFocused: (focused: boolean) => void;
  /** Set one field to the value it has in `q` (the panel). */
  setFieldValue: (field: Field, q: SearchQuery) => void;
  removeChip: (t: Token) => void;
  clearAll: () => void;
}

export function useDiscoverSearch(): DiscoverSearch {
  const location = useLocation();
  const navigate = useNavigate();

  const [text, setText] = useState(() => paramsToText(new URLSearchParams(location.search)));
  const [caret, setCaret] = useState<number | null>(null);
  const [editAt, setEditAt] = useState<number | null>(null);
  const [focused, setFocused] = useState(false);
  // The settled search: what is typed, once typing pauses. It starts settled,
  // so a search opened from a link runs at once.
  const [settled, setSettled] = useState<Settled>(() => settledOf(text));

  // Back/forward (or any outside navigation) changed the URL: read the text,
  // and the search, from it again. A change to the URL is told apart from
  // our own writes by `written`, the last search this hook put there. The
  // router may commit the new location in a later render than the one that
  // records the write (it navigates in a transition), so only a change of
  // the location itself is checked, never the two states against each other.
  const here = norm(location.search);
  const [seenSearch, setSeenSearch] = useState(here);
  const [written, setWritten] = useState(here);
  if (here !== seenSearch) {
    setSeenSearch(here);
    if (here !== written) {
      const fromUrl = paramsToText(new URLSearchParams(location.search));
      setWritten(here);
      setText(fromUrl);
      setEditAt(null);
      setSettled(settledOf(fromUrl));
    }
  }

  // A link's ids are shown by name once their labels arrive, unless the user
  // is in the box (rewriting it there would move the caret).
  const focusedRef = useRef(focused);
  useEffect(() => { focusedRef.current = focused; }, [focused]);
  const onLabel = useCallback((kind: EntityKind, id: string, name: string) => {
    if (!focusedRef.current) setText((t) => renameEntity(t, kind, id, name));
  }, []);
  const resolver = useEntityResolver(onLabel);

  const parsed = useMemo(
    () => parseSearch(text, { lookup: resolver.lookup, caret: focused ? caret : null, editAt: focused ? editAt : null }),
    [text, resolver.lookup, focused, caret, editAt],
  );
  const { want } = resolver;
  useEffect(() => { want(parsed.tokens); }, [want, parsed.tokens]);

  const key = queryKey(parsed.query);
  const empty = isEmptyQuery(parsed.query);
  const held = parsed.pending || parsed.problems.length > 0;

  const latest = useRef<Settled>({ key, query: parsed.query });
  useEffect(() => { latest.current = { key, query: parsed.query }; });

  useEffect(() => {
    if (held || key === settled.key) return;
    const timer = setTimeout(() => {
      const next = latest.current;
      setSettled(next);
      const search = norm(queryToSearch(next.query));
      if (search !== seenSearch) {
        // A new history entry when the filters change; typing replaces it.
        const replace = filtersKey(next.query) === filtersKey(settled.query);
        setWritten(search);
        navigate({ pathname: DISCOVER_PATH, search }, { replace });
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [key, held, settled, seenSearch, navigate]);

  const results = useModelSearch(settled.key, !isEmptyQuery(settled.query));

  const type = useCallback((next: string, at: number) => {
    setText(next);
    setCaret(at);
    setEditAt(at);
  }, []);
  const replace = useCallback((next: string, at?: number) => {
    setText(next);
    setEditAt(null);
    if (at !== undefined) setCaret(at);
  }, []);
  const settle = useCallback(() => setEditAt(null), []);
  const onFocused = useCallback((f: boolean) => {
    setFocused(f);
    if (!f) setEditAt(null);
  }, []);

  const lookup = resolver.lookup;
  const setFieldValue = useCallback((field: Field, q: SearchQuery) => {
    setText((t) => setField(t, field, q, lookup));
    setEditAt(null);
  }, [lookup]);
  const removeChip = useCallback((t: Token) => {
    // a functionals or submodels chip is one alternative of the search (#2299)
    setText((cur) => (t.group !== undefined && (t.field === 'uses' || t.field === 'submodels')
      ? removeGroup(cur, t.field, t.group, lookup)
      : removeToken(cur, t.start)));
    setEditAt(null);
  }, [lookup]);
  const clearAll = useCallback(() => {
    setText((cur) => clearFilters(cur));
    setEditAt(null);
  }, []);

  const serverProblems = settled.key === key ? results.problems ?? [] : [];

  return {
    text,
    caret,
    parsed,
    chips: chipTokens(parsed),
    active: !empty,
    problems: [...tokenProblems(parsed), ...serverProblems],
    results,
    searching: !empty && !parsed.problems.length && (held || settled.key !== key || results.searching),
    settled: settled.query,
    resolver,
    type,
    replace,
    settle,
    setCaret,
    setFocused: onFocused,
    setFieldValue,
    removeChip,
    clearAll,
  };
}
